import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import StatusBadge from "@/components/StatusBadge";
import UploadModal from "@/components/UploadModal";
import Header from "@/components/Header";
import { Upload, FileText, Eye, History, Sprout, Calendar, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const STATUS_CARD_CLASS = {
  aprobado: "card-approved",
  pendiente: "card-pending",
  revisado: "card-pending",
  rechazado: "card-rejected",
  missing: "card-missing",
};

export default function ProductorDashboard() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [historyDoc, setHistoryDoc] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaign, setCampaign] = useState(null);
  const [currentCampaign, setCurrentCampaign] = useState(null);

  useEffect(() => {
    api.get("/campaigns").then(({ data }) => {
      setCampaigns(data.campaigns);
      setCurrentCampaign(data.current);
      if (!campaign) setCampaign(data.current);
    });
  }, []);

  const locked = campaign && currentCampaign && campaign !== currentCampaign;

  const load = async () => {
    if (!campaign) return;
    setLoading(true);
    try {
      const [a, b] = await Promise.all([
        api.get("/productor/documents-status", { params: { campaign } }),
        api.get("/dashboard/stats"),
      ]);
      setItems(a.data);
      setStats(b.data);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (campaign) load(); }, [campaign]);

  const openUpload = (type) => { setSelectedType(type); setModalOpen(true); };

  const downloadDoc = async (id, filename) => {
    const res = await api.get(`/documents/${id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = filename || "documento";
    a.click(); URL.revokeObjectURL(url);
  };

  const completionPct = stats ? Math.round((stats.approved / Math.max(stats.total_required, 1)) * 100) : 0;

  return (
    <div className="min-h-screen bg-stone-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
        {/* Greeting */}
        <div className="mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-paprika-50 text-paprika-600 text-xs font-medium border border-paprika-200">
            <Sprout className="h-3.5 w-3.5" />
            Panel de Productor
          </div>
          <h1 className="mt-3 text-3xl md:text-4xl font-heading font-bold text-stone-900">
            Hola, {user?.name?.split(" ")[0]} 
          </h1>
          <p className="mt-2 text-stone-600">Sube y consulta tu documentación obligatoria.</p>
        </div>

        {/* Stats summary */}
        {stats && (
          <Card className="p-6 md:p-8 rounded-2xl mb-8 border-stone-200 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
              <div className="flex-1">
                <div className="flex items-baseline gap-3">
                  <div className="text-4xl md:text-5xl font-heading font-bold text-stone-900">{completionPct}%</div>
                  <div className="text-stone-600">de cumplimiento</div>
                </div>
                <div className="mt-3 h-2 w-full bg-stone-100 rounded-full overflow-hidden">
                  <div className="h-full bg-primary transition-all duration-500" style={{ width: `${completionPct}%` }} />
                </div>
              </div>
              <div className="grid grid-cols-4 gap-3 md:gap-6 w-full md:w-auto">
                <Stat label="Aprobados" value={stats.approved} color="text-green-600" />
                <Stat label="Pendientes" value={stats.pending} color="text-amber-600" />
                <Stat label="Rechazados" value={stats.rejected} color="text-red-600" />
                <Stat label="Faltantes" value={stats.missing} color="text-stone-500" />
              </div>
            </div>
          </Card>
        )}

        {/* Document grid */}
        <h2 className="text-xl font-heading font-semibold text-stone-900 mb-4">Documentos requeridos</h2>
        {loading ? (
          <div className="text-stone-500">Cargando…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {items.map((it) => (
              <Card
                key={it.type.id}
                className={`p-5 rounded-xl border-stone-200 hover:shadow-md hover:border-stone-300 transition-all duration-200 ${STATUS_CARD_CLASS[it.status]}`}
                data-testid={`card-doctype-${it.type.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-heading font-semibold text-stone-900 leading-snug">{it.type.name}</h3>
                    <p className="mt-1 text-xs text-stone-500 line-clamp-2">{it.type.description}</p>
                  </div>
                  <div className="h-10 w-10 rounded-lg bg-stone-50 border border-stone-200 flex items-center justify-center shrink-0">
                    <FileText className="h-5 w-5 text-stone-600" />
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <StatusBadge status={it.status} />
                  {it.document?.uploaded_at && (
                    <span className="text-xs text-stone-500">
                      {new Date(it.document.uploaded_at).toLocaleDateString("es-ES")}
                    </span>
                  )}
                </div>
                {it.status === "rechazado" && it.document?.rejection_reason && (
                  <div className="mt-3 p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700">
                    Motivo: {it.document.rejection_reason}
                  </div>
                )}
                <div className="mt-4 flex gap-2">
                  {!locked && (
                    <Button
                      onClick={() => openUpload(it.type)}
                      className="flex-1 bg-primary hover:bg-primary-hover text-white"
                      data-testid={`btn-upload-${it.type.id}`}
                    >
                      <Upload className="h-4 w-4 mr-2" />
                      {it.document ? "Reemplazar" : "Subir"}
                    </Button>
                  )}
                  {it.document && (
                    <>
                      <Button variant="outline" size="icon" onClick={() => downloadDoc(it.document.id, it.document.filename)} title="Descargar" data-testid={`btn-download-${it.type.id}`}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="outline" size="icon" onClick={() => setHistoryDoc(it.document)} title="Historial" data-testid={`btn-history-${it.type.id}`}>
                        <History className="h-4 w-4" />
                      </Button>
                    </>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <UploadModal open={modalOpen} onOpenChange={setModalOpen} docType={selectedType} onUploaded={load} />

      <Dialog open={!!historyDoc} onOpenChange={(v) => !v && setHistoryDoc(null)}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-heading">Historial del documento</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            {historyDoc?.history?.map((h, i) => (
              <div key={i} className="flex items-start gap-3 text-sm border-l-2 border-stone-200 pl-3">
                <div>
                  <div className="font-medium text-stone-800 capitalize">{h.action}</div>
                  <div className="text-xs text-stone-500">{new Date(h.at).toLocaleString("es-ES")}</div>
                  {h.reason && <div className="text-xs text-red-600 mt-1">{h.reason}</div>}
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="text-center md:text-left">
      <div className={`text-2xl md:text-3xl font-heading font-bold ${color}`}>{value}</div>
      <div className="text-xs text-stone-500 mt-0.5">{label}</div>
    </div>
  );
}
